// Filters tab for Contacts page
import React, { useMemo } from 'react';
import { CollapseProps } from 'antd';
import { FormikProps } from 'formik';
import { MFilter } from '@Components/Base';
import { Input, Radio, SearchableSelect } from '@Components/Filters';
import {
  getWorkLocationOptions,
  getDepartmentProjectsList,
} from '@Store/selectors/dictionarySelector';
import { useAppSelector } from '@Hooks/index';
import { Option } from '@Components/types/options';
import { WorkLocationTypeCodes } from '@Enum/index';
import { validateField } from '@Features/ContactPage/utils/validationSchema';

interface ContactFiltersForm {
  search: string;
  employeeNumber: string;
  employeeName: string;
  occupation: string;
  email: string;
  mobile: string;
  extension: string;
  workLocation: string;
  department: string;
  departmentType?: 'department' | 'project';
}

interface ContactFiltersProps {
  formik: FormikProps<ContactFiltersForm>;
  showError?: boolean;
  onClearAll?: () => void; // Add onClearAll prop
}

interface DepartmentProjectItem {
  projDeptID: number;
  projDeptName: string;
  wlId: number;
}

const ContactFilters: React.FC<ContactFiltersProps> = ({
  formik,
  showError = false,
  onClearAll, // Add onClearAll parameter
}) => {
  // Get work location options and department/project list from selectors
  const workLocationOptions = useAppSelector(getWorkLocationOptions);
  const departmentProjectsList = useAppSelector(getDepartmentProjectsList);

  // Check if work location is selected and not 'all'
  const isWorkLocationSelected =
    formik.values.workLocation && formik.values.workLocation !== 'all';

  // Create work location options with proper labels
  const workLocationStringOptions: Option[] = useMemo(() => {
    const options = [{ label: 'All', value: 'all' }];

    // Map the work location types to proper labels
    workLocationOptions.forEach((option) => {
      if (option.value === WorkLocationTypeCodes.DEPARTMENT) {
        options.push({ label: 'Head Office', value: option.value.toString() });
      } else if (option.value === WorkLocationTypeCodes.PROJECT) {
        options.push({ label: 'Projects', value: option.value.toString() });
      }
    });

    return options;
  }, [workLocationOptions]);

  // Dynamic department/project options based on work location
  const departmentProjectOptions = useMemo(() => {
    const workLocationId = formik.values.workLocation;

    if (workLocationId && workLocationId !== 'all') {
      const wlId = parseInt(workLocationId, 10);
      const filteredProjects = departmentProjectsList.filter(
        (item: DepartmentProjectItem) => item.wlId === wlId
      );

      // Add "All" option as the first option
      const options = [{ label: 'All', value: 'all' }];

      // Add the actual department/project options
      const projectOptions = filteredProjects.map(
        (project: DepartmentProjectItem) => ({
          label: project.projDeptName,
          value: project.projDeptID.toString(),
        })
      );

      return [...options, ...projectOptions];
    }
    // Return empty array when no work location is selected
    return [];
  }, [formik.values.workLocation, departmentProjectsList]);

  // Reset department/project when work location changes
  React.useEffect(() => {
    if (formik.values.workLocation && formik.values.workLocation !== 'all') {
      // Clear department selection when work location changes
      formik.setFieldValue('department', 'all');
      formik.setFieldValue('departmentType', undefined);
    } else if (formik.values.workLocation === 'all') {
      // Clear department when "All" is selected
      formik.setFieldValue('department', 'all');
      formik.setFieldValue('departmentType', undefined);
    }
  }, [formik.values.workLocation, departmentProjectsList]);

  // Clear department when work location is not selected
  React.useEffect(() => {
    if (!isWorkLocationSelected && formik.values.department) {
      formik.setFieldValue('department', 'all');
      formik.setFieldValue('departmentType', undefined);
    }
  }, [isWorkLocationSelected, formik.values.department]);

  // Add this useEffect to handle department type when department value changes
  React.useEffect(() => {
    const departmentValue = formik.values.department;
    if (departmentValue && departmentValue !== 'all') {
      const workLocationId = parseInt(formik.values.workLocation, 10);
      const selectedDeptProj = departmentProjectsList.find(
        (item: DepartmentProjectItem) =>
          item.projDeptID.toString() === departmentValue &&
          item.wlId === workLocationId
      );

      if (selectedDeptProj) {
        // Determine if it's a department or project based on work location type
        if (workLocationId === WorkLocationTypeCodes.DEPARTMENT) {
          formik.setFieldValue('departmentType', 'department');
        } else if (workLocationId === WorkLocationTypeCodes.PROJECT) {
          formik.setFieldValue('departmentType', 'project');
        }
      }
    } else {
      formik.setFieldValue('departmentType', undefined);
    }
  }, [
    formik.values.department,
    formik.values.workLocation,
    departmentProjectsList,
  ]);

  // Get the appropriate placeholder text based on work location
  const getDepartmentProjectPlaceholder = (): string => {
    if (!isWorkLocationSelected) {
      return 'Please select work location first';
    }

    const workLocationId = parseInt(formik.values.workLocation, 10);
    if (workLocationId === WorkLocationTypeCodes.DEPARTMENT) {
      return 'Select department';
    }
    if (workLocationId === WorkLocationTypeCodes.PROJECT) {
      return 'Select project';
    }

    return 'Select department/project';
  };

  const items: CollapseProps['items'] = [
    {
      key: 'EMPLOYEE_INFO',
      label: 'Employee Information',
      children: (
        <>
          <Input<ContactFiltersForm>
            formik={formik}
            formikKey="employeeNumber"
            placeholder="Enter employee number"
            validateField={validateField}
          />
          <Input<ContactFiltersForm>
            formik={formik}
            formikKey="employeeName"
            placeholder="Enter employee name"
            validateField={validateField}
          />
          <Input<ContactFiltersForm>
            formik={formik}
            formikKey="occupation"
            placeholder="Enter occupation"
            validateField={validateField}
          />
          <Input<ContactFiltersForm>
            formik={formik}
            formikKey="email"
            placeholder="Enter email"
            validateField={validateField}
          />
          <Input<ContactFiltersForm>
            formik={formik}
            formikKey="mobile"
            placeholder="Enter mobile"
            validateField={validateField}
          />
          <Input<ContactFiltersForm>
            formik={formik}
            formikKey="extension"
            placeholder="Enter extension"
            validateField={validateField}
          />
        </>
      ),
    },
    {
      key: 'WORK_LOCATION',
      label: 'Work Location',
      children: (
        <Radio<ContactFiltersForm>
          formik={formik}
          formikKey="workLocation"
          options={workLocationStringOptions}
        />
      ),
    },
    {
      key: 'DEPARTMENT',
      label: 'Department/Project',
      children: (
        <SearchableSelect<ContactFiltersForm>
          formik={formik}
          formikKey="department"
          options={departmentProjectOptions}
          placeholder={getDepartmentProjectPlaceholder()}
          disabled={!isWorkLocationSelected}
          showSearch
          filterOption
        />
      ),
    },
  ];

  return (
    <MFilter<ContactFiltersForm>
      showError={showError}
      formik={formik}
      items={items}
      onClearAll={onClearAll} // Pass onClearAll to MFilter
    />
  );
};

export default ContactFilters;
